import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Container } from '@mui/material';
import { PlayCircleOutline } from '@mui/icons-material';
import HowItWorksSection from '../components/HowItWorksSection';
import AUHeader from '../components/AUHeader';
import ReviewsSection from '../components/ReviewsSection';
import VideoAccessForm from '../components/VideoAccessForm';
import {
  trackSalesVideoPlay,
  trackFunnelHomeView,
  trackProceedToQualificationForm,
} from '../utils/metaPixel';
import { getEmailFromVideoAccess, trackInternalFunnelEvent } from '../utils/funnelTracking';
import { getOrCreateAnonymousId } from '../utils/anonymousId';
import { trackVisit } from '../utils/trackVisit';
import { submitLead } from '../utils/submitLead';
import {
  getStoredVideoAccess,
  hasValidVideoAccess,
  setStoredVideoAccess,
  updateStoredVideoAccess,
} from '../utils/videoAccessStorage';

const YOUTUBE_VIDEO_ID = '9vsjQdeueDY';
const HOME_VIEW_TRACKED_KEY = 'funnelHomeViewTracked';

const FOTOS = [
  '/Transformaciones%20AU/DiegoOsman.jpg',
  '/Transformaciones%20AU/lukas1.jpg',
  '/Transformaciones%20AU/luis1.JPG',
  //'/Transformaciones%20AU/Fares.jpg',
  '/Transformaciones%20AU/Michel.jpg',
  '/Transformaciones%20AU/Lucas.jpg',
  '/Transformaciones%20AU/Alejandro.jpg',
  '/Transformaciones%20AU/Alfonso1.jpg',
  '/Transformaciones%20AU/Ian.jpg',
  '/Transformaciones%20AU/Carlos.jpg',
  '/Transformaciones%20AU/Maca1.jpg',
  '/Transformaciones%20AU/MatiasOrtiz.jpg',
  '/Transformaciones%20AU/Matias.jpg',
  '/Transformaciones%20AU/JavierConcha.jpg',
  '/Transformaciones%20AU/Vale1.jpg',
  '/Transformaciones%20AU/alvaro1.jpg',
  '/Transformaciones%20AU/JuanRod.jpg',
  '/Transformaciones%20AU/gab1.JPG',
  '/Transformaciones%20AU/jacobo-1.JPG',
  '/Transformaciones%20AU/jacobo-2.JPG',
  '/Transformaciones%20AU/jacobo-3.JPG',
  '/Transformaciones%20AU/joaco1.JPG',
  '/Transformaciones%20AU/jaime2.JPG',
  '/Transformaciones%20AU/jaime1.JPG',
  '/Transformaciones%20AU/seba1.JPG',
  '/Transformaciones%20AU/seba2.JPG',
  '/Transformaciones%20AU/sebam2.JPG',
  '/Transformaciones%20AU/sebam1.JPG',
  '/Transformaciones%20AU/felipe2.JPG',
  '/Transformaciones%20AU/felipe1.JPG',
  '/Transformaciones%20AU/dani1.JPG',
  '/Transformaciones%20AU/tomas1.JPG',
  '/Transformaciones%20AU/tomas2.JPG',
  '/Transformaciones%20AU/joe1.JPG',
  '/Transformaciones%20AU/joe2.JPG',
  '/Transformaciones%20AU/joe3.JPG',
  '/Transformaciones%20AU/carlos1.JPG',
  '/Transformaciones%20AU/carlos2.JPG',
  '/Transformaciones%20AU/carlos3.JPG',
  '/Transformaciones%20AU/carlos4.JPG',
  '/Transformaciones%20AU/carlos5.JPG',
  '/Transformaciones%20AU/carlos6.JPG',
  '/Transformaciones%20AU/dufeu1.jpg',
  '/Transformaciones%20AU/dufeu2.jpg',
];

const CTAButton = ({ videoUnlocked, onGoToForm, onOpenForm, bg = 'transparent', py = 3 }) => {
  const handleClick = () => {
    if (videoUnlocked) {
      onGoToForm();
    } else {
      onOpenForm();
    }
  };
  return (
    <Box sx={{ bgcolor: bg, py, display: 'flex', justifyContent: 'center' }}>
      <Button
        onClick={handleClick}
        variant="contained"
        sx={{
          bgcolor: '#1A7FFF',
          '&:hover': { bgcolor: '#1568D9' },
          px: 5, py: 1.75, borderRadius: 2, minWidth: 220,
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          textTransform: 'none', lineHeight: 1,
          boxShadow: '0 4px 20px rgba(26,127,255,0.35)',
          gap: 0,
        }}
      >
        <Typography sx={{ fontFamily: 'Kanit, sans-serif', fontWeight: 700, fontSize: '1.15rem', color: '#fff', lineHeight: 1.2 }}>
          Empieza ahora
        </Typography>
        <Typography sx={{ fontFamily: 'Kanit, sans-serif', fontWeight: 400, fontSize: '0.72rem', color: 'rgba(255,255,255,0.7)', letterSpacing: 0.5 }}>
          Ver Planes
        </Typography>
      </Button>
    </Box>
  );
};

function FotoAnimada({ src }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return (
    <Box
      ref={ref}
      component="img"
      src={src}
      alt=""
      // El IntersectionObserver de arriba solo maneja la animacion: sin esto el
      // navegador se baja las 41 fotos apenas monta React, aunque el visitante
      // nunca llegue al carrusel. Con lazy solo pide las que se van viendo.
      loading="lazy"
      decoding="async"
      sx={{
        // Las transformaciones son cuadradas: en 3/4 se perdia un 25% del
        // ancho y quedaban cortados los brazos de las poses de doble biceps.
        height: { xs: 260, md: 320 },
        width: 'auto',
        aspectRatio: '1/1',
        objectFit: 'cover',
        flexShrink: 0,
        borderRadius: 1.5,
        display: 'block',
        opacity: visible ? 1 : 0,
        transform: visible ? 'scale(1) translateY(0)' : 'scale(0.92) translateY(14px)',
        transition: 'opacity 0.45s ease, transform 0.45s ease',
      }}
    />
  );
}

const HomePage = () => {
  const navigate = useNavigate();
  const [videoUnlocked, setVideoUnlocked] = useState(() => hasValidVideoAccess());
  const [showFormDialog, setShowFormDialog] = useState(false);
  const youtubePlayerRef = useRef(null);
  const youtubeApiReadyRef = useRef(false);
  const videoPlayTrackedRef = useRef(false);
  const homeViewTrackedRef = useRef(false);

  const fotosShuffled = useMemo(() => {
    const arr = [...FOTOS];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }, []);

  // ── tracking home view ──────────────────────────────────────────────────────
  useEffect(() => {
    if (homeViewTrackedRef.current) return;
    let hasSaved = false;
    try {
      hasSaved = hasValidVideoAccess();
      const trackedState = sessionStorage.getItem(HOME_VIEW_TRACKED_KEY);
      const currentState = hasSaved ? 'true' : 'false';
      if (trackedState === currentState) { homeViewTrackedRef.current = true; return; }
      sessionStorage.setItem(HOME_VIEW_TRACKED_KEY, currentState);
    } catch (_) {}
    homeViewTrackedRef.current = true;
    trackFunnelHomeView({ hasVideoLeadSaved: hasSaved });
  }, []);

  // ── tracking visita ─────────────────────────────────────────────────────────
  useEffect(() => {
    trackVisit();
  }, []);

  // ── YouTube player ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!videoUnlocked) return;
    const iframe = document.getElementById('youtube-video-iframe');
    if (!iframe || youtubePlayerRef.current) return;

    const tryTrack = (player) => {
      if (videoPlayTrackedRef.current) return;
      if (!player || typeof player.getPlayerState !== 'function') return;
      if (player.getPlayerState() === window.YT?.PlayerState?.PLAYING) {
        videoPlayTrackedRef.current = true;
        trackSalesVideoPlay();
        const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
        trackInternalFunnelEvent(backendUrl, 'view_video', {}, {
          email: getEmailFromVideoAccess(),
          anonymousId: getOrCreateAnonymousId(),
        });
      }
    };

    const initPlayer = () => {
      if (window.YT && window.YT.Player && iframe && !youtubePlayerRef.current) {
        try {
          youtubePlayerRef.current = new window.YT.Player(iframe, {
            events: {
              onReady: (e) => {
                const p = e.target;
                if (typeof p.setPlaybackRate === 'function') {
                  try { p.setPlaybackRate(1.2); } catch (_) {}
                }
                tryTrack(p);
                [0, 200, 600, 1200].forEach((ms) => setTimeout(() => tryTrack(p), ms));
              },
              onStateChange: (e) => {
                if (e.data === window.YT.PlayerState.PLAYING) tryTrack(e.target);
              },
            },
          });
          youtubeApiReadyRef.current = true;
        } catch (_) {}
      }
    };

    if (window.YT && window.YT.Player) {
      initPlayer();
    } else {
      window.onYouTubeIframeAPIReady = initPlayer;
      const t = setInterval(() => {
        if (window.YT && window.YT.Player) { clearInterval(t); initPlayer(); }
      }, 300);
      return () => clearInterval(t);
    }
  }, [videoUnlocked]);

  const handleVideoClick = () => { if (!videoUnlocked) setShowFormDialog(true); };

  const handleFormSubmit = async (data) => {
    setVideoUnlocked(true);
    setShowFormDialog(false);
    setStoredVideoAccess(data);

    // Dispara el Pixel, manda las señales de Meta y reintenta si el backend falla.
    await submitLead({ data });
  };

  const handleGoToForm = () => {
    let videoMinute = null;
    try {
      if (youtubePlayerRef.current && typeof youtubePlayerRef.current.getCurrentTime === 'function') {
        const s = youtubePlayerRef.current.getCurrentTime();
        if (typeof s === 'number' && !Number.isNaN(s)) videoMinute = Math.floor(s / 60);
      }
    } catch (_) {}
    const videoAccessData = getStoredVideoAccess();
    if (videoAccessData) updateStoredVideoAccess({ videoMinute });
    const hadPlayer = !!(youtubePlayerRef.current && typeof youtubePlayerRef.current.getCurrentTime === 'function');
    trackProceedToQualificationForm({ videoMinute, hadPlayer });
    const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    trackInternalFunnelEvent(backendUrl, 'proceed_to_form', { video_minute: videoMinute ?? null, had_player: hadPlayer }, {
      email: getEmailFromVideoAccess(),
      anonymousId: getOrCreateAnonymousId(),
    });
    navigate('/form');
  };

  // ── render ──────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ bgcolor: '#fff', fontFamily: 'Kanit, sans-serif' }}>

      <AUHeader />

      {/* ── Hero + VSL ── */}
      <Box sx={{ bgcolor: '#fff', pt: { xs: 3.5, md: 5 }, pb: 0 }}>
      <Container maxWidth="lg">
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 2.5, color: '#1A7FFF', textTransform: 'uppercase', mb: 1.5, fontFamily: 'Kanit, sans-serif' }}>
            AU Coaching Services
          </Typography>
          <Typography
            component="h1"
            sx={{ fontFamily: 'Kanit, sans-serif', fontWeight: 800, fontSize: { xs: '2.2rem', md: '3rem' }, lineHeight: 1.08, color: '#0F1117', mb: 1.5 }}
          >
            El sistema que rompe el estancamiento y hace que cualquier persona logre su{' '}
            <Box component="span" sx={{ color: '#1A7FFF' }}>mejor físico</Box>.
          </Typography>
          <Typography sx={{ fontFamily: 'Kanit, sans-serif', fontSize: { xs: '1rem', md: '1.1rem' }, color: '#64748B', lineHeight: 1.65, mb: 0 }}>
            Pierde grasa y gana masa muscular de forma{' '}
            <Box component="span" sx={{ fontWeight: 700, color: '#0F1117' }}>estratégica, sostenible y medible</Box>.{' '}
            <Box component="span" sx={{ fontWeight: 700, color: '#1A7FFF' }}>Sin improvisar ni perder tiempo.</Box>
          </Typography>
        </Box>

        {/* ── VSL ── */}
        <Box sx={{ pb: 0 }}>
        <Box
          id="video-demo"
          sx={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden', borderRadius: 3, boxShadow: '0 4px 24px rgba(0,0,0,0.12)' }}
        >
          <iframe
            id="youtube-video-iframe"
            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }}
            src={`https://www.youtube.com/embed/${YOUTUBE_VIDEO_ID}?rel=0&modestbranding=1&enablejsapi=1${videoUnlocked ? '&autoplay=1' : ''}`}
            title="AU Coaching Services"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
          {!videoUnlocked && (
            <Box
              onClick={handleVideoClick}
              sx={{
                position: 'absolute', inset: 0,
                bgcolor: 'rgba(0,0,0,0.72)',
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', zIndex: 10, borderRadius: 3,
                transition: 'background 0.2s',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.82)' },
              }}
            >
              <PlayCircleOutline sx={{ fontSize: { xs: 80, md: 100 }, color: '#fff', mb: 1.5, transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.08)' } }} />
              <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1.1rem', fontFamily: 'Kanit, sans-serif' }}>
                Haz click para ver el video
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.8rem', mt: 0.5, fontFamily: 'Kanit, sans-serif' }}>
                Completa un breve formulario
              </Typography>
            </Box>
          )}
        </Box>
        </Box>
      </Container>
      </Box>

      <CTAButton videoUnlocked={videoUnlocked} onGoToForm={handleGoToForm} onOpenForm={() => setShowFormDialog(true)} />

      {/* ── Transformaciones ── */}
      <Box sx={{ bgcolor: '#F8FAFC', width: '100%', pt: { xs: 3, md: 4 }, pb: 0 }}>
        <Container maxWidth="lg">
        <Box sx={{ mb: 2, px: 0 }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 2.5, color: '#1A7FFF', textTransform: 'uppercase', mb: 0.75, fontFamily: 'Kanit, sans-serif' }}>
            Resultados reales
          </Typography>
          <Typography component="h2" sx={{ fontFamily: 'Kanit, sans-serif', fontWeight: 800, fontSize: { xs: '1.6rem', md: '2rem' }, color: '#0F1117', mb: 2 }}>
            Transformaciones.
          </Typography>
          {/* Chips */}
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2.5 }}>
            {[
              ['8 Años', 'Experiencia Coaching 1:1'],
              ['100+', 'Cuerpos Transformados'],
            ].map(([val, label]) => (
              <Box key={label} sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', bgcolor: '#fff', border: '1px solid #E2E8F0', borderRadius: 2, px: 2, py: 1 }}>
                <Typography sx={{ fontFamily: 'Kanit, sans-serif', fontWeight: 800, fontSize: '1.1rem', color: '#1A7FFF', lineHeight: 1 }}>{val}</Typography>
                <Typography sx={{ fontFamily: 'Kanit, sans-serif', fontSize: '0.7rem', color: '#64748B', mt: 0.3 }}>{label}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
        </Container>
        <Container maxWidth="lg" sx={{ overflow: 'hidden', px: { xs: 2.5, md: 3 } }}>
          <Box sx={{
            display: 'flex', gap: '4px',
            overflowX: 'auto', pb: 2,
            '&::-webkit-scrollbar': { display: 'none' },
            scrollbarWidth: 'none',
          }}>
            {fotosShuffled.map((src, i) => (
              <FotoAnimada key={src} src={src} />
            ))}
          </Box>
        </Container>
      </Box>

      {/* ── Reviews ── */}
      <ReviewsSection />

      <CTAButton videoUnlocked={videoUnlocked} onGoToForm={handleGoToForm} onOpenForm={() => setShowFormDialog(true)} bg="#0F1117" py={3} />

      {/* ── Coach ── (TODO: terminar diseño)
      <Box sx={{ bgcolor: '#fff', px: { xs: 2.5, md: 4 }, py: { xs: 4, md: 6 }, maxWidth: 860, mx: 'auto' }}>
        ...
      </Box>
      */}

      {/* ── Cómo funciona ── */}
      <Box sx={{ '& > div': { pb: { xs: 2, md: 3 } } }}>
        <HowItWorksSection />
      </Box>

      <CTAButton videoUnlocked={videoUnlocked} onGoToForm={handleGoToForm} onOpenForm={() => setShowFormDialog(true)} bg="#fff" py={2} />

      {/* ── VideoAccessForm ── */}
      <VideoAccessForm
        open={showFormDialog}
        onClose={() => setShowFormDialog(false)}
        onSubmit={handleFormSubmit}
      />
    </Box>
  );
};

export default HomePage;
